import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../components/ui/Button";
import { authenticatedFetch } from "../../../auth/auth";

function apiBase() {
  return (
    import.meta.env.VITE_API_BASE_URL ||
    import.meta.env.VITE_API_URL ||
    ""
  ).replace(/\/$/, "");
}

function pct(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return "0%";
  return `${n}%`;
}

export default function CampaignPerformanceCard() {
  const [tab, setTab] = useState("conversao");
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState("");

  const subtitle = useMemo(() => {
    if (tab === "abertura") return "Aberturas via pixel (quando o cliente abre o e-mail)";
    if (tab === "rejeicao") return "Erros de envio (SMTP / endereço inválido / etc.)";
    return "Efeito geral das cobranças por e-mail";
  }, [tab]);

  async function load() {
    setLoading(true);
    setError("");

    try {
      const res = await authenticatedFetch(`${apiBase()}/api/email/dashboard`, { cache: "no-store" });
      const json = await res.json();
      if (!json?.ok) throw new Error(json?.message || "Falha ao carregar dashboard de e-mail.");
      setMetrics(json.metrics || null);
    } catch (e) {
      setError(e?.message || "Falha ao carregar dashboard de e-mail.");
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const sent = metrics?.sent ?? 0;
  const opened = metrics?.opened ?? 0;
  const openRate = metrics?.openRate ?? 0;
  const errors = metrics?.errors ?? 0;
  const rejectRate = metrics?.rejectRate ?? 0;

  return (
    <div className="bg-card rounded-lg border border-border p-6 card-shadow">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading-semibold text-lg text-card-foreground">
            Performance das Campanhas
          </h3>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            className={`text-xs px-3 py-1 rounded-full border ${tab === "conversao" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground"}`}
            onClick={() => setTab("conversao")}
          >
            Conversão
          </button>
          <button
            className={`text-xs px-3 py-1 rounded-full border ${tab === "abertura" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground"}`}
            onClick={() => setTab("abertura")}
          >
            Abertura
          </button>
          <button
            className={`text-xs px-3 py-1 rounded-full border ${tab === "rejeicao" ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground"}`}
            onClick={() => setTab("rejeicao")}
          >
            Rejeição
          </button>

          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? "Atualizando..." : "Atualizar"}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-lg border border-border bg-accent/30 p-4 text-sm text-muted-foreground">
          {error}
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-xs text-muted-foreground">E-mails Enviados</div>
            <div className="text-xl font-heading-semibold text-card-foreground">{loading ? "—" : sent}</div>
          </div>

          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-xs text-muted-foreground">Taxa de Abertura</div>
            <div className="text-xl font-heading-semibold text-card-foreground">
              {loading ? "—" : pct(openRate)}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-xs text-muted-foreground">Conversão Média</div>
            <div className="text-xl font-heading-semibold text-card-foreground">
              {loading ? "—" : "0%"}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-background p-4">
            <div className="text-xs text-muted-foreground">Pagamentos</div>
            <div className="text-xl font-heading-semibold text-card-foreground">
              {loading ? "—" : 0}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 rounded-lg border border-border bg-background p-4">
        {tab === "abertura" && (
          <div className="text-sm text-muted-foreground">
            Abertos: <span className="text-card-foreground font-body-medium">{opened}</span> •
            Taxa: <span className="text-card-foreground font-body-medium">{pct(openRate)}</span>
          </div>
        )}

        {tab === "rejeicao" && (
          <div className="text-sm text-muted-foreground">
            Erros: <span className="text-card-foreground font-body-medium">{errors}</span> •
            Taxa: <span className="text-card-foreground font-body-medium">{pct(rejectRate)}</span>
          </div>
        )}

        {tab === "conversao" && (
          <div className="text-sm text-muted-foreground">
            Conversão fica em 0% por enquanto (ainda não estamos “amarrando” pagamento do título ao envio do e-mail).
          </div>
        )}
      </div>
    </div>
  );
}
